import { Button, Spinner, TextInput } from "flowbite-react";
import { AnimatePresence, motion } from "framer-motion";
import { FloppyDiskIcon, Icon, X } from "@phosphor-icons/react";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface AnimatedModalProps {
  show: boolean;
  onClose: () => void;
  title?: string | ReactNode;
  description?: string | ReactNode;
  icon?: Icon;
  children?: ReactNode;
  footer?: ReactNode;
  closeButton?: boolean;
  isConfirmation?: boolean;
  onConfirm?: () => void;
  confirmText?: string;
  isConfirming?: boolean;
  cancelText?: string;
  confirmButtonColor?:
  | "red"
  | "blue"
  | "green"
  | "indigo"
  | "purple"
  | "yellow"
  | "orange"
  | "pink"
  | "gray"
  | "teal"
  | "lime"
  | "fuchsia"
  | "violet"
  | "cyan"
  | "emerald";
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "full" | "half";
  disableBackdropClick?: boolean;
  bodyClassName?: string;
  confirmationText?: string;
  confirmationTypeLabel?: string | ReactNode;
}

const spinnerColorMap: Record<string, string> = {
  red: "failure",
  blue: "info",
  green: "success",
  yellow: "warning",
  pink: "pink",
  purple: "purple",
  indigo: "purple",
  orange: "warning",
  gray: "gray",
  teal: "info",
  lime: "success",
  fuchsia: "pink",
  violet: "purple",
  cyan: "info",
  emerald: "success",
};

function getSpinnerColor(confirmButtonColor: string) {
  return spinnerColorMap[confirmButtonColor] || "info";
}

const AnimatedModal = ({
  show,
  onClose,
  title,
  description,
  icon: Icon,
  children,
  footer,
  closeButton = true,
  isConfirmation = false,
  onConfirm,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmButtonColor = "blue",
  size = "md",
  disableBackdropClick = false,
  isConfirming = false,
  bodyClassName,
  confirmationText,
  confirmationTypeLabel,
}: AnimatedModalProps) => {
  const { t } = useTranslation();
  const [userConfirmation, setUserConfirmation] = useState("");

  useEffect(() => {
    if (!show) setUserConfirmation("");
  }, [show]);

  const getModalSize = () => {
    switch (size) {
      case "sm":
        return "sm:max-w-sm";
      case "lg":
        return "sm:max-w-2xl";
      case "xl":
        return "sm:max-w-4xl";
      case "2xl":
        return "sm:max-w-6xl";
      case "full":
        return "w-full h-full sm:max-w-none sm:h-auto";
      case "half":
        return "w-full sm:max-w-2xl h-1/2";
      default:
        return "sm:max-w-lg";
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !disableBackdropClick) {
        onClose();
      }
    };

    if (show) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [show, onClose, disableBackdropClick]);

  return (
    <AnimatePresence mode="wait">
      {show && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center overflow-hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm shadow-2xl"
            onClick={!disableBackdropClick ? onClose : undefined}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 350,
            }}
            className={`relative z-[1000] w-full mx-4 overflow-hidden rounded-3xl bg-white shadow-[0_24px_48px_-12px_rgba(0,0,0,0.12)] ${getModalSize()}`}
            onClick={(e) => e.stopPropagation()}
          >


            <div className="flex flex-col max-h-[90vh]">
              <div className="p-6 flex justify-between items-center">
                {title && typeof title === "string" ? (
                  <div className="flex items-center gap-2.5">
                    {Icon && (
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-50 text-gray-700">
                        <Icon size={20} weight="duotone" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 leading-none tracking-tight">
                        {t(title, title)}
                      </h3>
                      {description && (
                        <div className="text-gray-500 text-xs mt-1.5 font-medium leading-relaxed">
                          {typeof description === "string" ? t(description, description) : description}
                        </div>
                      )}
                    </div>
                  </div>
                ) : title}
                {closeButton && (
                  <Button
                    color={"light"}
                    onClick={onClose}
                    className="rounded-full h-8 w-8 flex items-center justify-center hover:rotate-180 transition-all duration-300 "
                  >
                    <X size={20} weight="bold" className="text-red-400" />
                  </Button>
                )}
              </div>

              <div className={`flex-grow overflow-y-auto px-6 py-6 pt-3 ${bodyClassName || ""}`}>
                {children}
              </div>

              {isConfirmation ? (
                <div className="px-6 py-5 bg-gray-50/80 backdrop-blur-md border-t border-gray-100 flex flex-col gap-4">
                  {confirmationText && (
                    <div className="flex flex-col gap-2.5">
                      <label className="text-[10px] font-extrabold uppercase tracking-widest text-gray-400 px-1">
                        {confirmationTypeLabel || (
                          <>
                            {t("common.confirmTypeAction", "Please type '{{text}}' to confirm", { text: confirmationText })}
                          </>
                        )}
                      </label>
                      <TextInput
                        type="text"
                        placeholder={confirmationText}
                        value={userConfirmation}
                        sizing="md"
                        autoFocus
                        onChange={(e) => setUserConfirmation(e.target.value)}
                        onPaste={(e) => e.preventDefault()}
                        onDrop={(e) => e.preventDefault()}
                      />
                    </div>
                  )}
                  <div className="flex gap-2.5">
                    <Button
                      type="button"
                      color="light"
                      size="md"
                      className="rounded-xl transition-all w-full"
                      onClick={onClose}
                      disabled={isConfirming}
                    >
                      <span className="font-bold text-gray-500">{t(cancelText || "common.cancel", cancelText)}</span>
                    </Button>
                    <Button
                      type="button"
                      color={confirmButtonColor}
                      className="rounded-xl shadow-lg transition-all w-full"
                      onClick={onConfirm}
                      size="md"
                      processingSpinner={
                        <Spinner
                          size="md"
                          color={getSpinnerColor(confirmButtonColor)}
                        />
                      }
                      isProcessing={isConfirming}
                      disabled={
                        (!!confirmationText && userConfirmation !== confirmationText) || isConfirming
                      }
                    >
                      <div className="flex items-center gap-2">
                        {!isConfirming && <FloppyDiskIcon size={20} />}
                        <span className="font-bold">{t(confirmText || "common.confirm", confirmText)}</span>
                      </div>
                    </Button>
                  </div>
                </div>
              ) : (
                footer && (
                  <div className="px-6 py-5 bg-gray-50/80 backdrop-blur-md border-t border-gray-100">
                    {footer}
                  </div>
                )
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AnimatedModal;
